import axiosInstance from './AxiosService';
import axios from 'axios';
import Cookies from 'js-cookie';

const API_URL =  'https://audit-ai.net/api'  
//const API_URL =   'http://localhost:8000/api'; 

// Create a public axios instance that doesn't use auth interceptors
const publicAxios = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    }
});

// Add a request interceptor for publicAxios to attach tokens when needed
publicAxios.interceptors.request.use(
    config => {
        // Get token from localStorage/cookies for authenticated endpoints
        if (config.url && (
            config.url.startsWith('/app-users/') || 
            config.url.startsWith('/applications/') ||
            config.url.startsWith('/hr/data/') ||
            config.url.startsWith('/process/')
        )) {
            // Get token from multiple sources
            let token = localStorage.getItem('token');
            
            // If no token from localStorage, check Cookies
            if (!token) {
                token = Cookies.get('token');
            }
            
            if (token) {
                console.log(`RequestService: Using token for public request to: ${config.url}`);
                config.headers['Authorization'] = `Bearer ${token}`;
            } else {
                console.warn(`RequestService: No token available for authenticated endpoint: ${config.url}`);
            }
        }
        
        return config;
    },
    error => {
        console.error('RequestService: Request interceptor error:', error);
        return Promise.reject(error);
    }
);

const RequestService = {


    //Request

    fetchRequestID: async () => {
        const response = await axiosInstance.get('request-id/');
        return response.data;
    },

    fetchRequests: async () => {
        const response = await axiosInstance.get('access/request');
        return response.data;
      },
    
    fetchRequestById: async (requestId) => {
        const response = await axiosInstance.get(`access/request/${requestId}/`);
        return response.data;
    },
    
    fetchRequestsByEmail: async (email) => {
        const response = await axiosInstance.get(`myaccess/request/email/${email}/`);
        return response.data;
    },
    
    fetchRequestsByCreator: async (email) => {
        console.log(`RequestService: Fetching requests by creator: ${email}`);
        try {
            const response = await publicAxios.get(`/myaccess/request/creator/${email}/`);
            console.log('RequestService: Response received:', response);
            return response.data;
        } catch (error) {
            console.error('RequestService: Error fetching requests by creator:', error);
            console.error('RequestService: Error details:', error.response ? error.response.data : 'No response data');
            throw error;
        }
    },
    
    sendAuthToken: async (email) => {
        const response = await publicAxios.post('/myaccess/request/auth-token/', { email });
        return response.data;
    },
    
    verifyAuthToken: async (email, token) => {
        const response = await publicAxios.post('/myaccess/request/verify-token/', { 
            email, 
            token 
        });
        return response.data;
    },
    
    // New methods for direct API access
    sendAuthTokenDirect: async (email) => {
        const response = await publicAxios.post('/myaccess/request/auth-token/', { email });
        return response.data;
    },
    
    verifyAuthTokenDirect: async (email, token) => {
        const response = await publicAxios.post('/myaccess/request/verify-token/', { 
            email, 
            token 
        });
        return response.data;
    },

    // New method to obtain a JWT token after email verification
    getJwtToken: async (email) => {
        try {
            console.log('RequestService: Obtaining JWT token for verified email:', email);
            const response = await publicAxios.post('/myaccess/request/jwt-token/', { 
                email,
                verification_type: 'email' 
            });
            
            if (response.data && response.data.access) {
                // Store the JWT tokens
                localStorage.setItem('token', response.data.access);
                if (response.data.refresh) {
                    localStorage.setItem('refresh_token', response.data.refresh);
                }
                console.log('RequestService: JWT token obtained and stored successfully');
                return response.data;
            } else {
                console.error('RequestService: Invalid token response format:', response.data);
                throw new Error('Invalid token response format');
            }
        } catch (error) {
            console.error('RequestService: Error obtaining JWT token:', error);
            console.error('RequestService: Error details:', error.response ? error.response.data : 'No response data');
            throw error;
        }
    },

    // Method to securely logout and invalidate tokens on the server
    logout: async (email) => {
        try {
            console.log('RequestService: Logging out and invalidating tokens for:', email);
            // Send request to invalidate tokens on the server
            const response = await publicAxios.post('/logout/', { email });
            
            // Clear client-side storage regardless of server response
            localStorage.removeItem('token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('accessRequestSession');
            sessionStorage.removeItem('verified_email');
            
            // Also clear cookies if the Cookies library is available
            if (typeof Cookies !== 'undefined') {
                Cookies.remove('token', { path: '/' });
                Cookies.remove('refresh_token', { path: '/' });
            }
            
            console.log('RequestService: Logout successful, all tokens cleared');
            return response.data;
        } catch (error) {
            console.error('RequestService: Error during logout:', error);
            // Still clear client-side storage even if the server request fails
            localStorage.removeItem('token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('accessRequestSession');
            sessionStorage.removeItem('verified_email');
            
            // Also clear cookies if the Cookies library is available
            if (typeof Cookies !== 'undefined') {
                Cookies.remove('token', { path: '/' });
                Cookies.remove('refresh_token', { path: '/' });
            }
            
            throw error;
        }
    },

    // New method to verify session with backend
    verifySession: async (email) => {
        try {
            console.log('RequestService: Verifying session for:', email);
            
            // First check if we have a valid JWT token
            const existingToken = localStorage.getItem('token');
            if (existingToken) {
                console.log('RequestService: Found existing JWT token, session is valid');
                return true;
            }
            
            // If no token, try to get a new one
            try {
                const tokenData = await this.getJwtToken(email);
                if (tokenData && tokenData.access) {
                    console.log('RequestService: Successfully obtained new JWT token');
                    return true;
                }
            } catch (tokenError) {
                console.error('RequestService: Error obtaining JWT token:', tokenError);
            }
            
            // If we get here, try the direct verification endpoint
            try {
                const response = await publicAxios.post('/myaccess/request/verify-session/', { email });
                return response.data && response.data.valid;
            } catch (error) {
                console.error('RequestService: Error with verify-session endpoint:', error);
                
                // If the endpoint doesn't exist or fails, fall back to email verification
                try {
                    // Try to send a new auth token - if successful, email exists
                    await this.sendAuthToken(email);
                    console.log('RequestService: Session validated via email verification');
                    return true;
                } catch (emailError) {
                    console.error('RequestService: Email verification failed:', emailError);
                    return false;
                }
            }
        } catch (error) {
            console.error('RequestService: Error verifying session:', error);
            return false;
        }
    },
    
    createRequest: async (requesData) => {
        const response = await axiosInstance.post(`access/request/`,requesData);
        return response.data;
      },
    
    updateRequest: async (requestId, requestData) => {
        const response = await axiosInstance.put(`access/request/${requestId}/`, requestData);
        return response.data;
      },
    
    deleteRequest: async (requestId) => {
        const response = await axiosInstance.delete(`access/request/${requestId}/`);
        return response.data;
      },


    //Approval

    fetchApproval: async () => {
        const response = await axiosInstance.get('access/approval');
        return response.data;
      },
    
    fetchApprovalById: async (approvalID) => {
        const response = await axiosInstance.get(`access/approval/${approvalID}/`);
        return response.data;
    },
    
    fetchApprovalByRequestId: async (requestID) => {
        try {
            const response = await publicAxios.get(`/myaccess/request/approval/${requestID}/`);
            return response.data;
        } catch (error) {
            console.error('RequestService: Error fetching approval by request ID:', error);
            console.error('RequestService: Error details:', error.response ? error.response.data : 'No response data');
            return null;
        }
    },
    
    createApproval: async (approvalData) => {
        const response = await axiosInstance.post(`access/approval/`,approvalData);
        return response.data;
      },

    confirmApproval: async (requestID, approvalData) => {
        const response = await axiosInstance.post(`accessrequest/approval/${requestID}/`,approvalData);
        return response.data;
      },
    
    updateApproval: async (approvalID, approvalData) => {
        const response = await axiosInstance.put(`access/approval/${approvalID}/`, approvalData);
        return response.data;
      },
    
    deleteApproval: async (approvalID) => {
        const response = await axiosInstance.delete(`access/approval/${approvalID}/`);
        return response.data;
      },

    //EMAIL
    sendEmailApproval: async (approvalData) => {
        console.log('=== sendEmailApproval called ===');
        console.log('Sending data:', JSON.stringify(approvalData, null, 2));
        try {
            const response = await axiosInstance.post('send-approval-request/', approvalData);
            console.log('sendEmailApproval success:', response.data);
            return response.data;
        } catch (error) {
            console.error('sendEmailApproval error:', error);
            if (error.response) {
                console.error('Error response data:', error.response.data);
                console.error('Error response status:', error.response.status);
            }
            throw error;
        }
    },

    //My Requests

    fetchRequestsByApp: async (requestID) => {
      const response = await axiosInstance.get(`access/myrequests/${requestID}/`);
      return response.data;
  },

    // Methods for approving/rejecting requests
    approveRequest: async (requestData) => {
        try {
            const data = {
                request_id: requestData.id,
                action: 'approve',
                email: requestData.approverEmail,
                comments: requestData.comments || ''
            };
            
            const response = await publicAxios.post('/myaccess/request/approve-reject/', data);
            return response.data;
        } catch (error) {
            console.error('RequestService: Error approving request:', error);
            console.error('RequestService: Error details:', error.response ? error.response.data : 'No response data');
            throw error;
        }
    },
    
    rejectRequest: async (requestData) => {
        try {
            if (!requestData.rejectionReason) {
                throw new Error('Rejection reason is required');
            }
            
            const data = {
                request_id: requestData.id,
                action: 'reject',
                email: requestData.approverEmail,
                comments: requestData.comments || '',
                rejection_reason: requestData.rejectionReason
            };
            
            const response = await publicAxios.post('/myaccess/request/approve-reject/', data);
            return response.data;
        } catch (error) {
            console.error('RequestService: Error rejecting request:', error);
            console.error('RequestService: Error details:', error.response ? error.response.data : 'No response data');
            throw error;
        }
    },
    
    // Method to delete a request by a user
    deleteUserRequest: async (requestId, userEmail) => {
        try {
            const response = await publicAxios.delete(`/myaccess/request/delete/${requestId}/`, {
                data: { email: userEmail }
            });
            return response.data;
        } catch (error) {
            console.error('RequestService: Error deleting request:', error);
            console.error('RequestService: Error details:', error.response ? error.response.data : 'No response data');
            throw error;
        }
    },
    
};

export default RequestService;
